import {
  $businessUnit,
  $tenant,
  enancheUrlContextWithViewHeaderDescription,
  getTag,
  SelectedPathIds,
  useIDTagging,
  useSitemap,
  useSitemapItems,
  useUrlContext,
} from '@msdk/common'
import {
  AreaGroup,
  CustomItem as CustomItemDef,
  EntityItem,
  FormOpenMode,
  readBaseMetadataByName,
  SitemapItem,
  SitemapReferenceItem,
  SitemapType,
  ViewItem,
} from '@msdk/definitions'
import { Collapse, IconSelector, List, Stack, styled, Tooltip } from '@msdk/ui'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { SidebarDivider } from 'components/SidebarDivider'
import { ReactElement } from 'react'
import { NavLink } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import { useNavigationMenu } from 'state/navigation-menu'

import { EmptyIcon } from './EmptyIcon'
import { SidebarListItemButton } from './SidebarItemButton'
import { SidebarListItemText } from './SidebarItemText'
import { SidebarListItem } from './SidebarListItem'
import { SidebarListItemIcon } from './SidebarListItemIcon'
import { isAreaOpen } from './utils'

type ItemMode = 'Extended' | 'Collapsed'

const StyledCollapse = styled(Collapse, { shouldForwardProp: (prop) => prop !== 'level' }) <{ level: number }>`
  gap: 0;
`

const Spacer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'level',
}) <{ level: number }>`
  height: ${(props) => {
    return props.level === 0 ? '8px' : '4px'
  }};
`

const StyledList = styled(List, {
  shouldForwardProp: (prop) => prop !== 'hasDescription' && prop !== 'level' && prop !== 'last',
}) <{ hasDescription?: boolean; level: number; last?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 0;
  margin: 0;
  margin-bottom: ${(props) => {
    if (props.hasDescription) return '0px'
    if (props.last) return 0
    return props.level === 0 ? '8px' : '4px'
  }};
`

const StyledSidebarDivider = styled(SidebarDivider)`
  margin-top: 12px;
  margin-bottom: 12px;
`

const isMatching = (item: SitemapItem, selectedPath?: SelectedPathIds) => {
  return selectedPath?.ids.includes(item.id)
}

const Area = ({
  area,
  level = 0,
  selectedPath,
  last,
  currentExpanded,
  onExpand,
}: {
  last?: boolean
  area: AreaGroup
  level?: number
  selectedPath: SelectedPathIds
  currentExpanded?: string
  onExpand: (id: string) => void
}) => {
  const { context } = useUrlContext()
  const { navigationMenuOpen } = useNavigationMenu()
  const _description = area.tDescription ? area.tDescription : area.description
  const hasDescription = !!_description

  const devEnabled = useIDTagging()
  const description = devEnabled ? `{${area.msName}-${area.msName}-${area.idml}}` : _description

  const open = isAreaOpen(area, currentExpanded)

  const handleClick = () => {
    onExpand(area.id)
  }

  const className = [isMatching(area, selectedPath) ? 'active' : '', open ? 'open' : ''].filter((c) => !!c).join(' ')

  if (!navigationMenuOpen) {
    return (
      <>
        {area.description && (
          <Tooltip title={description} placement="right">
            <SidebarListItemButton
              className={className}
              onClick={handleClick}
              data-test-id={getTag(context.sitemap, 'sidemenu_area_' + area.description, 'open', 'button')}
              level={level}
              last={last}
            >
              <SidebarListItemIcon>
                <IconSelector iconName={area.iconTag || 'FolderOpenOutlined'} />
              </SidebarListItemIcon>
            </SidebarListItemButton>
          </Tooltip>
        )}
        {!hasDescription && (
          <>
            <StyledList key={area.id} component="div" hasDescription={hasDescription} level={level} last={last}>
              {area.items.map((i, index) => (
                <ConditionalSitemapRenderer
                  key={i.id}
                  type={i.type}
                  item={i}
                  mode="Collapsed"
                  selectedPathIds={selectedPath}
                  level={level}
                  last={index === area.items.length - 1}
                  onExpand={onExpand}
                  currentExpanded={currentExpanded}
                />
              ))}
            </StyledList>
          </>
        )}
      </>
    )
  }

  let iconName = area.iconTag
  if (!iconName && level === 0) {
    iconName = 'FolderOpenOutlined'
  }

  return (
    <>
      {area.description && (
        <SidebarListItemButton
          className={className}
          onClick={handleClick}
          data-test-id={getTag(context.sitemap, 'sidemenu_area_' + area.description, 'open', 'button')}
          level={level}
          open={open}
          last={last}
        >
          {iconName ? (
            <SidebarListItemIcon>
              <IconSelector iconName={iconName} />
            </SidebarListItemIcon>
          ) : (
            <SidebarListItemIcon>
              <EmptyIcon data-test-id="empty-icon" />
            </SidebarListItemIcon>
          )}
          <Stack direction="row" alignItems="center" gap="8px">
            <SidebarListItemText primary={description} />
            {open && description ? <ExpandLess /> : <ExpandMore />}
          </Stack>
        </SidebarListItemButton>
      )}
      <StyledCollapse id="collapse" level={level} in={open || !hasDescription} timeout="auto" unmountOnExit>
        <StyledList key={area.id} component="div" hasDescription={hasDescription} level={level} last={last}>
          {area.items.map((item, i) => (
            <ConditionalSitemapRenderer
              last={i === area.items.length - 1}
              key={item.id}
              type={item.type}
              item={item}
              mode="Extended"
              level={description ? level + 1 : level}
              selectedPathIds={selectedPath}
              useDefaultIcon={!description}
              onExpand={onExpand}
              currentExpanded={currentExpanded}
            />
          ))}
        </StyledList>
      </StyledCollapse>
      {open && !last && (
        <>
          <StyledSidebarDivider />
          <Spacer level={level} />
        </>
      )}
    </>
  )
}

const Item = ({
  last,
  item,
  mode,
  level,
  useDefaultIcon,
  selectedPath,
}: {
  last?: boolean
  item: SitemapItem
  mode: ItemMode
  level: number
  useDefaultIcon?: boolean
  selectedPath: SelectedPathIds
}) => {
  const { context, serialize } = useUrlContext()
  const tenant = useRecoilValue($tenant)
  const { navigationMenuOpen, setNavigationMenu } = useNavigationMenu()
  const sitemapItems = useSitemapItems()
  const devEnabled = useIDTagging()

  const casted = item as ViewItem
  let sitemapItem: SitemapReferenceItem | undefined = undefined
  let firstItemEntity: string | undefined = undefined

  if (!casted.entity && !casted.viewName) {
    sitemapItem = sitemapItems.find((sItem) => sItem.id === item.id)
    firstItemEntity = sitemapItem?.entries[0]?.entity
  }

  let description = item.tDescription
  if (sitemapItem && devEnabled) description = `{${sitemapItem.msName}-${sitemapItem.msName}-${sitemapItem.idml}}`
  else if (devEnabled) description = `{${item.msName}-${item.msName}-${item.idml}}`
  else if (sitemapItem) description = sitemapItem.tDescription ?? sitemapItem.description
  else description = item.tDescription ?? item.description

  const viewHeaderDescription = casted.viewHeaderDescription ?? sitemapItem?.viewHeaderDescription
  const enanchedContext = enancheUrlContextWithViewHeaderDescription(context, viewHeaderDescription)

  if (enanchedContext['sId']) {
    // clean up any possible sitemap id that is old,
    // coming from another item
    delete enanchedContext['sId']
  }

  // Clean up any exitsing filters that my be for another view
  delete enanchedContext['f']
  delete enanchedContext['cf']

  const encoded = serialize(enanchedContext)

  let url = item.url
    ? item.url
    : `${tenant}/${casted.associatedSiteMap ?? enanchedContext.sitemap}/${casted.entity}/${casted.viewName
    }?s=${encoded}`

  if (sitemapItem) {
    url = `${tenant}/${sitemapItem.associatedSiteMap ?? enanchedContext.sitemap}/${sitemapItem.id}?s=${encoded}`
  }

  const className = [isMatching(item, selectedPath) ? 'active' : ''].filter((c) => !!c).join(' ')

  return (
    <>
      {mode === 'Extended' && (
        <SidebarListItem
          className={className}
          component={NavLink}
          onClick={() => setNavigationMenu(navigationMenuOpen)}
          to={url}
          data-test-id={getTag(enanchedContext.sitemap, 'sidemenu', casted.entity ?? firstItemEntity, 'button')}
          level={level}
          last={last}
        >
          {item.iconTag && (
            <SidebarListItemIcon>
              <IconSelector iconName={item.iconTag} />
            </SidebarListItemIcon>
          )}
          {!item.iconTag && !useDefaultIcon && (
            <SidebarListItemIcon>
              <EmptyIcon data-test-id="empty-icon" />
            </SidebarListItemIcon>
          )}
          {!item.iconTag && useDefaultIcon && (
            <SidebarListItemIcon>
              <IconSelector iconName="FolderOpenOutlined" />
            </SidebarListItemIcon>
          )}
          <SidebarListItemText primary={description} />
        </SidebarListItem>
      )}

      {mode === 'Collapsed' && (
        <Tooltip title={description} placement="right">
          <SidebarListItemButton
            onClick={() => setNavigationMenu(navigationMenuOpen)}
            data-test-id={getTag(enanchedContext.sitemap, 'sidemenu', casted.entity, 'button')}
            level={level}
            last={last}
            className={className}
          >
            <SidebarListItemIcon>
              <IconSelector iconName={item.iconTag || 'FolderOpenOutlined'} />
            </SidebarListItemIcon>
          </SidebarListItemButton>
        </Tooltip>
      )}
    </>
  )
}

const CustomItem = ({
  last,
  item,
  mode,
  level,
  useDefaultIcon,
  selectedPath,
}: {
  last?: boolean
  item: SitemapItem
  mode: ItemMode
  level: number
  useDefaultIcon?: boolean
  selectedPath: SelectedPathIds
}) => {
  const { context, serialize } = useUrlContext()
  const tenant = useRecoilValue($tenant)
  const encoded = serialize(context)
  const casted = item as CustomItemDef
  const { navigationMenuOpen, setNavigationMenu } = useNavigationMenu()

  const url = `${tenant}/${context.sitemap}/CUSTOM/${casted.name}?s=${encoded}`
  const idTaggingEnabled = useIDTagging()
  const description = idTaggingEnabled
    ? `{${item.msName}-${item.msName}-${item.idml}}`
    : item.tDescription
      ? item.tDescription
      : item.description

  const className = [isMatching(item, selectedPath) ? 'active' : ''].filter((c) => !!c).join(' ')

  return (
    <>
      {mode === 'Extended' && (
        <SidebarListItem
          className={className}
          component={NavLink}
          onClick={() => setNavigationMenu(navigationMenuOpen)}
          to={url}
          data-test-id={getTag(context.sitemap, 'sidemenu', casted.name, 'button')}
          level={level}
          last={last}
        >
          {item.iconTag && (
            <SidebarListItemIcon>
              <IconSelector iconName={item.iconTag} />
            </SidebarListItemIcon>
          )}
          {!item.iconTag && !useDefaultIcon && (
            <SidebarListItemIcon>
              <EmptyIcon data-test-id="empty-icon" />
            </SidebarListItemIcon>
          )}
          {!item.iconTag && useDefaultIcon && (
            <SidebarListItemIcon>
              <IconSelector iconName="FolderOpenOutlined" />
            </SidebarListItemIcon>
          )}
          <SidebarListItemText primary={description} />
        </SidebarListItem>
      )}
      {mode === 'Collapsed' && (
        <Tooltip title={item.description} placement="right">
          <SidebarListItemButton
            onClick={() => setNavigationMenu(navigationMenuOpen)}
            data-test-id={getTag(context.sitemap, 'sidemenu', casted.name, 'button')}
            level={level}
            last={last}
            className={className}
          >
            <SidebarListItemIcon>
              <IconSelector iconName={item.iconTag || 'FolderOpenOutlined'} />
            </SidebarListItemIcon>
          </SidebarListItemButton>
        </Tooltip>
      )}
    </>
  )
}

const EntitySitemapItem = ({
  last,
  item,
  mode,
  level,
  useDefaultIcon,
  selectedPath,
}: {
  last?: boolean
  item: SitemapItem
  mode: ItemMode
  level: number
  useDefaultIcon?: boolean
  selectedPath: SelectedPathIds
}) => {
  const { context, serialize } = useUrlContext()
  const sitemapModel = useSitemap(context.sitemap)
  const tenant = useRecoilValue($tenant)
  const businessUnit = useRecoilValue($businessUnit)
  const encoded = serialize(context)
  const casted = item as EntityItem
  const baseMetadata = readBaseMetadataByName(casted.entity)
  const id = sitemapModel.context === baseMetadata?.name ? context[baseMetadata.name] : null

  const parentId =
    baseMetadata?.parent === 'BUSINESS_UNIT'
      ? businessUnit
      : baseMetadata?.parent
        ? context[baseMetadata?.parent as string]
        : '00000000-0000-0000-0000-000000000000'

  const { navigationMenuOpen, setNavigationMenu } = useNavigationMenu()
  const formMode = item.formOpenMode === FormOpenMode.Edit ? 'Edit' : 'Readonly'
  const url = `${tenant}/${context.sitemap}/${casted.entity}/${casted.formName}/${id ?? parentId
    }?s=${encoded}&mode=${formMode}`

  const idTaggingEnabled = useIDTagging()
  const description = idTaggingEnabled
    ? `{${item.msName}-${item.msName}-${item.idml}}`
    : item.tDescription
      ? item.tDescription
      : item.description
  const className = [isMatching(item, selectedPath) ? 'active' : ''].filter((c) => !!c).join(' ')

  return (
    <>
      {mode === 'Extended' && (
        <SidebarListItem
          component={NavLink}
          to={url}
          onClick={() => setNavigationMenu(navigationMenuOpen)}
          data-test-id={getTag(context.sitemap, 'sidemenu', casted.formName, 'button')}
          level={level}
          last={last}
        >
          {item.iconTag && (
            <SidebarListItemIcon>
              <IconSelector iconName={item.iconTag} />
            </SidebarListItemIcon>
          )}
          {!item.iconTag && !useDefaultIcon && (
            <SidebarListItemIcon>
              <EmptyIcon data-test-id="empty-icon" />
            </SidebarListItemIcon>
          )}
          {!item.iconTag && useDefaultIcon && (
            <SidebarListItemIcon>
              <IconSelector iconName="FolderOpenOutlined" />
            </SidebarListItemIcon>
          )}
          <SidebarListItemText primary={description} />
        </SidebarListItem>
      )}
      {mode === 'Collapsed' && (
        <Tooltip title={item.description} placement="right">
          <SidebarListItemButton
            onClick={() => setNavigationMenu(navigationMenuOpen)}
            data-test-id={getTag(context.sitemap, 'sidemenu', casted.formName, 'button')}
            level={level}
            last={last}
            className={className}
          >
            <SidebarListItemIcon>
              <IconSelector iconName={item.iconTag || 'FolderOpenOutlined'} />
            </SidebarListItemIcon>
          </SidebarListItemButton>
        </Tooltip>
      )}
    </>
  )
}

export const ConditionalSitemapRenderer = ({
  type,
  selectedPathIds,
  currentExpanded,
  onExpand,
  ...props
}: {
  last?: boolean
  type: SitemapType
  item: SitemapItem
  mode: ItemMode
  minified?: boolean
  level: number
  selectedPathIds: SelectedPathIds
  useDefaultIcon?: boolean
  setOpenMenuFromAreaMinified?: undefined | (() => void)
  currentExpanded?: string
  onExpand: (id: string) => void
}) => {
  const renderer: Record<SitemapType, ReactElement> = {
    EntityItem: <EntitySitemapItem {...props} selectedPath={selectedPathIds} />,
    ViewItem: <Item {...props} selectedPath={selectedPathIds} />,
    CustomItem: <CustomItem {...props} selectedPath={selectedPathIds} />,
    ExternalUrl: <Item {...props} selectedPath={selectedPathIds} />,
    SmartAppItem: <Item {...props} selectedPath={selectedPathIds} />,
    SmartAppItemUr: <Item {...props} selectedPath={selectedPathIds} />,
    AreaGroup: (
      <Area
        {...props}
        area={props.item as AreaGroup}
        selectedPath={selectedPathIds}
        currentExpanded={currentExpanded}
        onExpand={onExpand}
      />
    ),
    // this is here for the compiler, but in reality it should never happen
    SiteMap: <></>,
  }

  return <>{renderer[type]}</>
}
